import React, { useState } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { getPortvilleApplicationDisplayStatus } from '../../../../utils/portvilleApplicationStatus';

interface PortvilleApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  userLevel: number;
  additionalRequest: string;
  applyCode?: string;
  approvedAt?: any;
  documents: {
    identification: string;
    employment: string;
    other?: string;
  };
  status: 'pending' | 'checked' | 'approved' | 'rejected' | 'consumed';
  consumedReason?: string;
  createdAt: any;
  updatedAt: any;
}

interface ApplicationTableProps {
  applications: PortvilleApplication[];
  loading: boolean;
  onRefresh: () => void;
  onSendSMS?: (application: PortvilleApplication, newStatus: string, cancelReason?: string) => Promise<void>;
  onApplicationOpen: (applicationId: string) => void;
}

const ApplicationTable: React.FC<ApplicationTableProps> = ({
  applications,
  loading,
  onRefresh,
  onSendSMS,
  onApplicationOpen,
}) => {
  const [updating, setUpdating] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelTargetApplication, setCancelTargetApplication] = useState<PortvilleApplication | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // userLevel을 등급명으로 변환하는 헬퍼 함수
  const getUserLevelLabel = (level: number): string => {
    const levelMap: { [key: number]: string } = {
      1: '최고 관리자',
      2: '일반 관리자',
      3: '공사 관계자',
      4: '센터 관계자',
      5: '입주업체',
      6: '입주협력업체',
      7: '공공기관',
      8: '항만근로자',
      9: '일반 사용자'
    };
    return levelMap[level] || '알 수 없음';
  };

	  const handleStatusUpdate = async (applicationId: string, newStatus: 'checked' | 'approved' | 'rejected', reason?: string) => {
	    try {
	      const application = applications.find(app => app.id === applicationId);
	      if (!application) return;

      if (newStatus === 'rejected' && !reason?.trim()) {
        alert('취소 사유를 입력해주세요.');
        return;
      }

      // checked 상태로 변경할 때만 userLevel 비교
      if (newStatus === 'checked') {
        const userDoc = await getDoc(doc(db, 'user_profiles', application.uid));
        const userData = userDoc.data();

        if (userData && userData.userLevel !== application.userLevel) {
          const currentLevelLabel = getUserLevelLabel(userData.userLevel);
          const applicationLevelLabel = getUserLevelLabel(application.userLevel);

          const confirmed = window.confirm(
            `회원등급이 다릅니다.\n\n` +
            `현재 등급: ${currentLevelLabel} (${userData.userLevel})\n` +
            `신청서 등급: ${applicationLevelLabel} (${application.userLevel})\n\n` +
            `검토처리를 진행하면 회원등급이 신청서 등급으로 변경됩니다.\n` +
            `계속 진행하시겠습니까?`
          );

          if (!confirmed) {
            return;
          }

          // userLevel 업데이트
          const userProfileRef = doc(db, 'user_profiles', application.uid);
          await updateDoc(userProfileRef, {
            userLevel: application.userLevel,
            updatedAt: new Date()
          });
        }
      }

      setUpdating(applicationId);
	      const applicationRef = doc(db, 'portville_applications', applicationId);
	      
	      // 상태 업데이트 전에 현재 시간을 저장
	      const updatedAt = new Date();

	      const updatePayload =
	        newStatus === 'approved'
	          ? { status: newStatus, updatedAt, approvedAt: updatedAt }
	          : newStatus === 'rejected'
	            ? { status: newStatus, updatedAt, rejectedAt: updatedAt, rejectionReason: reason?.trim() }
	            : { status: newStatus, updatedAt };

	      await updateDoc(applicationRef, updatePayload);

	      // 상태 변경 시 SMS 발송
	      if ((newStatus === 'checked' || newStatus === 'approved' || newStatus === 'rejected') && onSendSMS) {
	        const nextApplication: PortvilleApplication = {
	          ...application,
	          status: newStatus,
	          updatedAt,
	          ...(newStatus === 'approved' ? { approvedAt: updatedAt } : {})
	        };

	        await onSendSMS(nextApplication, newStatus, reason?.trim());
	      }

      if (newStatus === 'rejected') {
        setIsCancelModalOpen(false);
        setCancelTargetApplication(null);
        setCancelReason('');
      }

      // 상태 업데이트 성공 후 즉시 onRefresh 호출
      onRefresh();
    } catch (err) {
      console.error('상태 업데이트 오류:', err);
      alert('상태 업데이트 중 오류가 발생했습니다.');
    } finally {
      setUpdating(null);
    }
  };

  const openCancelModal = (application: PortvilleApplication) => {
    setCancelTargetApplication(application);
    setCancelReason('');
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    if (updating) return;
    setIsCancelModalOpen(false);
    setCancelTargetApplication(null);
    setCancelReason('');
  };

  const handleCancelSubmit = () => {
    if (!cancelTargetApplication) return;
    handleStatusUpdate(cancelTargetApplication.id, 'rejected', cancelReason);
  };

  const getStatusBadge = (application: PortvilleApplication) => {
    const displayStatus = getPortvilleApplicationDisplayStatus(application);
    switch (displayStatus.key) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">{displayStatus.label}</span>;
      case 'checked':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{displayStatus.label}</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">{displayStatus.label}</span>;
      case 'application_cancelled':
      case 'occupancy_cancelled':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">{displayStatus.label}</span>;
      case 'contracted':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">{displayStatus.label}</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">{displayStatus.label}</span>;
      default:
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[Portville][ApplicationTable] Unknown status:', {
            applicationId: application.id,
            rawStatus: application.status,
            consumedReason: application.consumedReason,
            uid: application.uid
          });
        }
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
            {displayStatus.label}
          </span>
        );
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">신청자</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">소속</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">연락처</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">신청일시</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {applications.map((application) => (
            <tr key={application.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onApplicationOpen(application.id)}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{application.name}</div>
                <div className="text-sm text-gray-500">{application.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{application.organization}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{application.phoneNumber}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatDate(application.createdAt)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(application)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                {application.status === 'pending' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusUpdate(application.id, 'checked')}
                      disabled={updating === application.id || !application.uid}
                      className={`px-3 py-1 rounded-md ${
                        !application.uid
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50'
                      }`}
                      title={!application.uid ? '사용자 정보가 없는 신청서는 검토처리할 수 없습니다.' : ''}
                    >
                      검토처리
                    </button>
                    <button
                      onClick={() => openCancelModal(application)}
                      disabled={updating === application.id}
                      className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                    >
                      취소
                    </button>
                  </div>
                ) : application.status === 'checked' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleStatusUpdate(application.id, 'approved')}
                      disabled={updating === application.id}
                      className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                    >
                      입주진행
                    </button>
                    <button
                      onClick={() => openCancelModal(application)}
                      disabled={updating === application.id}
                      className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                    >
                      취소
                    </button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    {formatDate(application.updatedAt)}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isCancelModalOpen && cancelTargetApplication && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm p-8 max-w-md w-full outline outline-1 outline-slate-200">
            <h3 className="text-lg font-bold text-[#1e2c49] font-['Pretendard'] mb-6">입주 신청 취소</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">
                  취소 사유 *
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard'] min-h-[120px]"
                  placeholder="입주 신청 취소 사유를 입력해주세요."
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                <p className="text-sm text-yellow-800 font-['Pretendard']">
                  신청을 취소하면 신청자에게 SMS로 취소 알림과 사유가 발송됩니다.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeCancelModal}
                  disabled={Boolean(updating)}
                  className="px-6 py-3 text-sm font-medium text-[#6b7684] bg-white border border-[#e5e8eb] rounded-sm hover:bg-[#f8fbff] font-['Pretendard'] transition-colors disabled:opacity-50"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubmit}
                  disabled={Boolean(updating)}
                  className="px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 font-['Pretendard'] transition-colors"
                >
                  {updating ? '처리 중...' : '신청 취소'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationTable; 
